import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
  CtBinaryOperator,
  CtButtonConfiguration,
  CtCardConfiguration,
  CtCardIconConfiguration,
  CtCardIconPosition,
  CtCardTemplate,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelDatatableOperators,
  CtModelService,
} from '@ctsolution/ct-framework';
import { CtManagerService } from '../../../_core/lib/ct-manager.service';
import { ItemHandlingRouteData } from '../../../_core/route-data/item-handling.route-data';
import { HandlingOriginEnum } from '../../../_core/enum/handling-origin.enum';
import { HandlingRouteData } from '../../../_core/route-data/handling.route-data';

@Injectable()
export class DashboardOrderHelper {
  constructor(
    private router: Router,
    private managerService: CtManagerService,
    private modelService: CtModelService<any>
  ) {}

  getOrderCounter(counterList: CtCardConfiguration[]) {
    this.getNotPricedOrderCounter(counterList);
  }

  getOrderCardList(
    counterList: CtCardConfiguration[],
    masterCardList: CtCardConfiguration[]
  ) {
    this.getDeliveredOrdersCurrentMonthCounter(counterList);
    this.getOrdersWithoutInvoiceCard(masterCardList);
    this.getOrdersInvoiceExpiringTodayCard(masterCardList);
  }

  private async getOrdersInvoiceExpiringTodayCard(
    masterCardList: CtCardConfiguration[]
  ) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'orders_invoice_expiring_today',
        ORDERS_INVOICE_EXPIRING_TODAY_CARD
      )
      .then((component) => {
        return;
      });

    // Calcola la data di oggi (inizio e fine giornata)
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    const filterString = `[InvoiceDueDate] >= ? AND [InvoiceDueDate] <= ?`;
    const filters = [
      CTModelDatatableFilter.create()
        .setField(filterString)
        .setValue([startOfToday.toISOString(), endOfToday.toISOString()])
        .setOperatorType(CtBinaryOperator.OrOperator),
    ];
    const result = await this.getHandlingsByFilter(filters);

    const count = result?.TotalElement ?? 0;
    ORDERS_INVOICE_EXPIRING_TODAY_CARD.setDescription(
      `${count > 0 ? count : '0'}`
    );

    const queryObject = {
      constants: [
        {
          Field: filterString,
          OperatorType: CtBinaryOperator.OrOperator,
          Value: [startOfToday, endOfToday],
        },
      ],
    };

    const queryString = JSON.stringify(queryObject);
    const urlTree: UrlTree = this.router.createUrlTree(
      ['/handling/order/list'],
      { queryParams: { q: queryString } }
    );
    const fullUrl = this.router.serializeUrl(urlTree);

    masterCardList.push(
      ORDERS_INVOICE_EXPIRING_TODAY_CARD.setAction(() =>
        window.open(fullUrl, '_blank')
      )
    );
  }

  private async getOrdersWithoutInvoiceCard(
    masterCardList: CtCardConfiguration[]
  ) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'orders_without_invoice',
        ORDERS_WITHOUT_INVOICE_AMOUNT_CARD
      )
      .then((component) => {
        return;
      });

    const filterString = `[InvoiceAmount] is null`;
    const filters = [
      CTModelDatatableFilter.create()
        .setField(filterString)
        .setValue([])
        .setOperatorType(CtBinaryOperator.OrOperator),
    ];
    const result = await this.getHandlingsByFilter(filters);

    const count = result?.TotalElement ?? 0;
    ORDERS_WITHOUT_INVOICE_AMOUNT_CARD.setDescription(
      `${count > 0 ? count : '0'}`
    );

    const queryObject = {
      constants: [
        {
          Field: filterString,
          OperatorType: CtBinaryOperator.OrOperator,
          Value: [],
        },
      ],
    };

    const queryString = JSON.stringify(queryObject);
    const urlTree: UrlTree = this.router.createUrlTree(
      ['/handling/order/list'],
      { queryParams: { q: queryString } }
    );
    const fullUrl = this.router.serializeUrl(urlTree);

    masterCardList.push(
      ORDERS_WITHOUT_INVOICE_AMOUNT_CARD.setAction(() =>
        window.open(fullUrl, '_blank')
      )
    );
  }

  private async getNotPricedOrderCounter(counterList: CtCardConfiguration[]) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'order_counter_notPriced',
        ORDER_NOTPRICED_COUNTER
      )
      .then((component) => {
        return;
      });

    const filters = [
      CTModelDatatableFilter.create()
        .setField('[Price] is null OR [Price] = ?')
        .setValue([0])
        .setOperatorType(CtBinaryOperator.OrOperator),

      CTModelDatatableFilter.create()
        .setField('Handling.HandlingOrigin')
        .setValue(HandlingOriginEnum.Supplier)
        .setOperatorType(CtBinaryOperator.Equal),
    ];

    const resultProspect = await this.getItemHandlingsByFilter(filters);

    ORDER_NOTPRICED_COUNTER.setDescription(
      (resultProspect?.TotalElement ?? 0) > 0
        ? resultProspect.TotalElement
        : '0'
    );

    const queryObject = {
      constants: [
        {
          Field: '[Price] is null OR [Price] = ?',
          OperatorType: CtBinaryOperator.OrOperator,
          Value: [0],
        },
        {
          Field: 'Handling.HandlingOrigin',
          OperatorType: CtBinaryOperator.Equal,
          Value: HandlingOriginEnum.Supplier,
        },
      ],
    };

    const queryString = JSON.stringify(queryObject);
    const urlTree: UrlTree = this.router.createUrlTree(
      ['/item-handling/list'],
      { queryParams: { q: queryString } }
    );

    // Convertiamo l'UrlTree in una stringa e apriamo una nuova scheda
    const fullUrl = this.router.serializeUrl(urlTree);

    counterList.push(
      ORDER_NOTPRICED_COUNTER.setAction(() => window.open(fullUrl, '_blank'))
    );
  }

  private async getDeliveredOrdersCurrentMonthCounter(
    counterList: CtCardConfiguration[]
  ) {
    this.managerService
      .getComponentOverride(
        'dashboard',
        'orders_delivered_current_month',
        ORDERS_DELIVERED_CURRENT_MONTH_COUNTER
      )
      .then((component) => {
        return;
      });

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0,
      23,
      59,
      59
    );

    const filterString = '[DeliveryDate] >= ? AND [DeliveryDate] <= ?';

    const filters = [
      CTModelDatatableFilter.create()
        .setField(filterString)
        .setValue([startOfMonth.toISOString(), endOfMonth.toISOString()])
        .setOperatorType(CtBinaryOperator.OrOperator),
    ];

    const resultProspect = await this.getHandlingsByFilter(filters);

    ORDERS_DELIVERED_CURRENT_MONTH_COUNTER.setDescription(
      (resultProspect?.TotalElement ?? 0) > 0
        ? resultProspect.TotalElement
        : '0'
    );

    const queryObject = {
      constants: [
        {
          Field: filterString,
          OperatorType: CtBinaryOperator.OrOperator,
          Value: [startOfMonth, endOfMonth],
        },
      ],
    };

    const queryString = JSON.stringify(queryObject);
    const urlTree: UrlTree = this.router.createUrlTree(
      ['/handling/order/list'],
      { queryParams: { q: queryString } }
    );
    const fullUrl = this.router.serializeUrl(urlTree);

    counterList.push(
      ORDERS_DELIVERED_CURRENT_MONTH_COUNTER.setAction(() =>
        window.open(fullUrl, '_blank')
      )
    );
  }

  private getItemHandlingsByFilter(filters: Array<CTModelDatatableFilter>) {
    const configuration: CtModelConfiguration<any> =
      CtModelConfiguration.create().setRouteData(ItemHandlingRouteData());

    const operators: CtModelDatatableOperators =
      CtModelDatatableOperators.create().setFilters(filters);

    return new Promise<CtModelConfiguration<Array<any>>>((resolve) => {
      this.modelService
        .getList(configuration, operators)
        .subscribe((response) => resolve(response.Result));
    });
  }
  private getHandlingsByFilter(filters: Array<CTModelDatatableFilter>) {
    const configuration: CtModelConfiguration<any> =
      CtModelConfiguration.create().setRouteData(HandlingRouteData());

    const operators: CtModelDatatableOperators =
      CtModelDatatableOperators.create().setFilters(filters);

    return new Promise<CtModelConfiguration<Array<any>>>((resolve) => {
      this.modelService
        .getList(configuration, operators)
        .subscribe((response) => resolve(response.Result));
    });
  }
}

export const ORDER_NOTPRICED_COUNTER: CtCardConfiguration =
  CtCardConfiguration.create()
    .setTitle('dashboard.orderNotPriced')
    .setVisible(true)
    .setTemplate(CtCardTemplate.COUNTER)
    .setClass('bg-warning text-white');

export const ORDERS_DELIVERED_CURRENT_MONTH_COUNTER: CtCardConfiguration =
  CtCardConfiguration.create()
    .setTitle('dashboard.ordersDeliveredCurrentMonth')
    .setVisible(false)
    .setTemplate(CtCardTemplate.COUNTER)
    .setClass('bg-warning text-white');

export const ORDERS_WITHOUT_INVOICE_AMOUNT_CARD: CtCardConfiguration =
  CtCardConfiguration.create()
    .setTitle('dashboard.ordersWithoutInvoice')
    .setVisible(false)
    .setIconSetting(
      CtCardIconConfiguration.create()
        .setIcon('build')
        .setPosition(CtCardIconPosition.SUFFIX)
        .setClass("bg-white text-danger")
    )
    .setClass('bg-danger text-white')
    .setButton(CtButtonConfiguration.create().setIcon('arrow_forward'));

export const ORDERS_INVOICE_EXPIRING_TODAY_CARD: CtCardConfiguration =
  CtCardConfiguration.create()
    .setTitle('dashboard.ordersExpiringToday')
    .setVisible(false)
    .setIconSetting(
      CtCardIconConfiguration.create()
        .setIcon('notifications_active')
        .setPosition(CtCardIconPosition.SUFFIX)
        .setClass("bg-white text-success")
    )
    .setClass('bg-success text-white')
    .setButton(CtButtonConfiguration.create().setIcon('arrow_forward'));
